import Manager from 'coreClasses/Manager';
import LanguageModel from './LanguageModel';
import {receiveLanguages, selectLanguages} from 'coreStore/data/configurations/index';
import {getDefaultSite} from 'coreUtils/siteUtils';
import CountryModel from 'coreClasses/countries/CountryModel';

type DirectionType = 'ltr' | 'rtl';

/**
 * Represents LanguagesMgr
 */
class LanguagesMgr extends Manager {
	private cache = {
		languages: [],
	};

	/**
	 * @description Returns an object with the current language code and direction
	 * @param {String} languageCode Current language code
	 * @returns {Object}
	 */
	getLanguageAttributes(languageCode: string = '') {
		const code = languageCode || this.managerRepository.getLocalesMgr().getCurrentLocale().getLanguageCode();
		let language;

		try {
			language = this.getLanguageByCode(code);
		} catch (e) {
			const defaultLanguageCode = this.managerRepository.getLocalesMgr().getDefaultLocale().getLanguageCode();

			language = this.getLanguageByCode(defaultLanguageCode);
		}

		const direction: DirectionType = language.getIsRtlLanguage() ? 'rtl' : 'ltr';

		return {
			code,
			direction,
			isRtl: language.getIsRtlLanguage(),
			isLtr: language.getIsLtrLanguage(),
		};
	}

	/**
	 * Fetches all languages
	 */
	async fetchAllLanguages(): Promise<void> {
		const {locales} = this.managerRepository.getStaticCacheMgr().getLocales()
		|| await this.managerRepository.getSfccAPI().getAllLocales();

		const languages = locales.map((language) => ({
			id: language.id
		}));

		// reset cache on new data fetching
		this.cache.languages = [];
		this.managerRepository.getStoreMgr().getStore().dispatch(receiveLanguages(languages));
	}

	/**
	 * Returns all languages
	 * @returns {Array<LanguageModel>} languages
	 */
	getLanguages(): LanguageModel[] {
		if (this.cache.languages.length > 0) {
			return this.cache.languages;
		}

		this.cache.languages = selectLanguages(this.managerRepository.getStoreMgr().getStore().getState())?.map(
			(language) => new LanguageModel(language)
		) || [];

		return this.cache.languages;
	}

	/**
	 * Returns the language model by language ID
	 * @param {string} languageID - language ID
	 * @returns {LanguageModel} language model
	 */
	getLanguageByCode(languageID: string): LanguageModel {
		return this.getLanguages().find((language) => language.language.id === languageID);
	}

	/**
	 * Returns the default language model
	 * @returns {LanguageModel} language model
	 */
	getDefaultLanguage(): LanguageModel {
		const defaultLanguage = this.managerRepository.getConfigurationsMgr().get('defaultLocale');
		const {defaultLocale} = getDefaultSite();
		const defaultLanguageCode = defaultLanguage?.id || defaultLocale;

		const language = this.getLanguages().find(
			(language: LanguageModel) => language.getLanguageCode() === defaultLanguageCode
		);

		if (language) {
			return language;
		}

		const defaultSite = getDefaultSite();

		return new LanguageModel({
			id: defaultSite.defaultLocale,
			default: true
		});
	}

	/**
	 * Returns if the language is present on the site
	 * if the country has available languages set, it will check if this is available
	 * @param {string} language - language ID
	 * @param {CountryModel} country - the country model for the country
	 * @returns {boolean}
	 */
	isLocaleCodeSupported(language: string, country: CountryModel): boolean {
		const siteAvailableLanguages = this.getLanguages().map((lang) => lang.language.id);

		if (siteAvailableLanguages.includes(language)) {
			if (country.getAvailableLanguages().length > 0) {
				return country.getAvailableLanguages().includes(language);
			}

			return true;
		}

		return false;
	}
}

export default LanguagesMgr;
